import React from 'react';
import { moment } from '@eb/date';
import memoize from 'lodash/memoize';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import map from 'lodash/map';
import pick from 'lodash/pick';
import { getListOfTimes } from '@eb/eds-utils';
import { timePropType } from '@eb/eds-utils';
import { AutocompleteField } from '@eb/eds-autocomplete-field';
import { InputField } from '@eb/eds-input-field';
import { VALID_INPUT_FORMATS, DISPLAY_FORMAT } from './constants';

import './timePicker.scss';

const parseValue = (value) => {
    if (value && value.trim()) {
        const parsedValue = moment(value, VALID_INPUT_FORMATS);

        if (parsedValue.isValid()) {
            return {
                value,
                parsedValue,
                formattedValue: parsedValue.format(DISPLAY_FORMAT),
            };
        }
    }

    return {
        value,
        parsedValue: undefined,
        formattedValue: '',
    };
};

const getTimeSuggestions = memoize((minTime, maxTime) =>
    map(getListOfTimes(minTime, maxTime), (time) => ({
        content: time,
        value: time,
    })),
);

const getFilteredTimeSuggestions = (minTime, maxTime, queryValue) =>
    getTimeSuggestions(minTime, maxTime).filter(
        (time) =>
            !queryValue ||
            !queryValue.trim() ||
            time.content.startsWith(queryValue),
    );

const DROPDOWN_CONTAINER_CLASS = 'eds-timepicker__dropdown-wrapper';

const DropdownContentContainer = ({ children }) => (
    <div className={DROPDOWN_CONTAINER_CLASS}>{children}</div>
);

export default class TimePicker extends React.PureComponent {
    static propTypes = {
        /**
         * Value of text input
         */
        value: PropTypes.string,
        /**
         * Default value of text input
         */
        defaultValue: PropTypes.string,
        /**
         * ID of element
         */
        id: PropTypes.string,
        /**
         * Name of element
         */
        name: PropTypes.string,
        /**
         * Minimum time allowed.
         */
        minTime: timePropType,
        /**
         * Maximum time allowed.
         */
        maxTime: timePropType,
        /**
         * Is required
         */
        required: PropTypes.bool,
        /**
         * Set error state
         */
        hasError: PropTypes.bool,
        /**
         * {function} onChange
         */
        onChange: PropTypes.func,
        /**
         * {function} onBlur
         */
        onBlur: PropTypes.func,
        /**
         * {function} onFocus
         */
        onFocus: PropTypes.func,
        /**
         * Is disabled
         */
        disabled: PropTypes.bool,
        /**
         * Default placeholder for input field
         */
        placeholder: translationPropType,
        /**
         * If the timepicker should use v2 Autocomplete which utilizes
         * InputField isntead of TextInput
         */
        isV2: PropTypes.bool,
    };

    static defaultProps = {
        defaultValue: '',
    };

    constructor(props, context) {
        super(props, context);

        const state = parseValue(props.value || props.defaultValue);

        this.state = {
            searchQuery: '',
            ...state,
            // Initialize value formatted
            value: state.formattedValue,
        };
    }

    componentDidUpdate = ({ value: prevValue }) => {
        const { value } = this.props;

        // Do not suddenly update the input if the user is typing.
        if (value !== prevValue && !this.state.isFocus) {
            this.setState(parseValue(value));
        }
    };

    _handleItemSelect = (value) => {
        this.setState(parseValue(value), () => {
            const { onChange } = this.props;
            const { parsedValue } = this.state;

            if (onChange) {
                onChange(value, parsedValue);
            }
        });
    };

    _handleBlur = () => {
        const { onBlur } = this.props;

        const newState = {
            isFocus: false,
            searchQuery: '',
        };

        if (this.state.formattedValue) {
            newState.value = this.state.formattedValue;
        }

        this.setState(newState, () => {
            const { formattedValue, parsedValue } = this.state;

            if (onBlur) {
                onBlur(formattedValue, parsedValue);
            }
        });
    };

    _handleFocus = () => {
        const { onFocus } = this.props;
        const { value, parsedValue } = this.state;

        this.setState({
            isFocus: true,
        });

        if (onFocus) {
            onFocus(value, parsedValue);
        }
    };

    _handleChange = (value) => {
        this.setState(
            {
                searchQuery: value,
                ...parseValue(value),
            },
            () => {
                const { onChange } = this.props;
                const { parsedValue } = this.state;

                if (onChange) {
                    onChange(value, parsedValue);
                }
            },
        );
    };

    render() {
        const {
            id,
            name,
            required,
            hasError,
            placeholder,
            disabled,
            minTime,
            maxTime,
            isV2,
        } = this.props;
        let inputProps = {};

        if (isV2) {
            inputProps = pick(this.props, ...Object.keys(InputField.propTypes));
        }

        return (
            <div className="eds-timepicker">
                <AutocompleteField
                    {...inputProps}
                    id={id}
                    name={name}
                    value={this.state.value}
                    suggestions={
                        this.state.isFocus
                            ? getFilteredTimeSuggestions(
                                  minTime,
                                  maxTime,
                                  this.state.searchQuery,
                              )
                            : []
                    }
                    DropdownContentContainer={DropdownContentContainer}
                    autoscrollToSelected={true}
                    scrollContainerSelector={`.${DROPDOWN_CONTAINER_CLASS}`}
                    required={required}
                    hasError={hasError}
                    onSelect={this._handleItemSelect}
                    onChange={this._handleChange}
                    onBlur={this._handleBlur}
                    onFocus={this._handleFocus}
                    isLimitedSearch={true}
                    placeholder={placeholder}
                    disabled={disabled}
                    isV2={isV2}
                />
            </div>
        );
    }
}
